import axios, { AxiosInstance } from 'axios'

import { PaySlipForReleaseDTO } from '@/DTOs/PaySlipForReleaseDTO'

const httpClient = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL,
})

interface PaySlip {
  id: string
  date: string
  employee: string
}

type PaySlipsResponse = PaySlip[]

interface User {
  id: string
  name: string
  email: string
}

interface PaySlipForRelease {
  id: string
  employeeName: string
  employeeEmail: string
  date: string
  isReleased: boolean
}

type PaySlipsForReleaseResponse = PaySlipForRelease[]

class ApiClient {
  public constructor(private readonly client: AxiosInstance) {
    client.interceptors.request.use(config => {
      const token = document.cookie
        .split('; ')
        .find(row => row.startsWith('auth-token'))
        ?.split('=')[1]
      if (token) config.headers.Authorization = `Bearer ${token}`

      return config
    })
  }

  public async whoAmI(): Promise<User> {
    const { data } = await this.client.get<User>('/who-am-i')

    return data
  }

  public async getPaySlips(): Promise<PaySlipDTO[]> {
    const { data } = await this.client.get<PaySlipsResponse>('/pay-slips')

    return data.map(i => ({
      ...i,
      date: new Date(Date.parse(i.date)),
    }))
  }

  public async getPaySlipsForRelease(): Promise<PaySlipForReleaseDTO[]> {
    const { data } = await this.client.get<PaySlipsForReleaseResponse>('/admin/pay-slips')

    return data.map(i => ({
      ...i,
      date: new Date(Date.parse(i.date)),
    }))
  }

  public async releasePaySlip(id: string): Promise<PaySlipForReleaseDTO> {
    const { data } = await this.client.get<PaySlipForRelease>(`/admin/pay-slips/${id}/release`)

    return {
      ...data,
      date: new Date(Date.parse(data.date)),
    }
  }

  public async unreleasePaySlip(id: string): Promise<PaySlipForReleaseDTO> {
    const { data } = await this.client.get<PaySlipForRelease>(
      `/admin/pay-slips/${id}/unrelease`,
    )

    return {
      ...data,
      date: new Date(Date.parse(data.date)),
    }
  }

  public async trashPaySlip(id: string): Promise<void> {
    await this.client.delete(`/admin/pay-slips/${id}`)
  }

  public async downloadPaySlip(id: string): Promise<Blob> {
    const { data } = await this.client.get(`/pay-slips/${id}`, {
      responseType: 'blob',
    })

    return new Blob([data])
  }

  public async uploadFile(file: File): Promise<FileUploadResponseDTO> {
    const formData = new FormData()
    formData.append('file', file)
    const { data } = await this.client.post<FileUploadResponseDTO>('/pay-slips', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

    return data
  }

  public async pollStatus(statusToken: string): Promise<PdfParsingStatusDTO> {
    const { data } = await this.client.get<PdfParsingStatusDTO>(
      `/pay-slips/status/${statusToken}`,
    )

    return data
  }

  public async allUsers(): Promise<UserDTO[]> {
    const { data } = await this.client.get<UserDTO[]>('/users')

    return data
  }

  public async updateUser(user: Pick<UserDTO, 'id' | 'email' | 'isAdmin'>): Promise<UserDTO> {
    const { data } = await this.client.put<UserDTO>(`/users/${user.id}`, {
      ...user,
    })

    return data
  }

  public async mergeUsers(from: Pick<UserDTO, 'id'>, to: Pick<UserDTO, 'id'>): Promise<void> {
    await this.client.put<void>(`/users/${from.id}/merge`, {
      mergeTo: to.id,
    })
  }
}

export const client = new ApiClient(httpClient)
